import React, { ChangeEvent, FC, FormEvent, useMemo } from "react";
import { Controller, ControllerRenderProps } from "react-hook-form";

import { VerticalSpace } from "components/VerticalSpace/VerticalSpace";
import { NamedInput } from "components/NamedInput/NamedInput";
import { AppSelect } from "components/Select/Select";
import { NamedCheckbox } from "components/NamedCheckbox/NamedCheckbox";

import styles from "./styles.module.css";
import { FormItemsValues, ProductPropertiesDescriptionProps } from "./types";

const PRICE_REGEX = /^\d*(\d+[\.,])?\d*$/;

export const ProductPropertiesDescription: FC<
  ProductPropertiesDescriptionProps
> = ({
  properties,
  control,
  groupOptions,
  productImages,
  ndsOptions,
  measureOptions,
  getValues,
}) => {
  const filteredImages = useMemo(() => {
    return productImages.filter((_, index) => index > 0 && index < 4);
  }, [productImages]);

  const handlePriceChange = (
    e: ChangeEvent<HTMLInputElement> | FormEvent<HTMLInputElement>,
    field: ControllerRenderProps<FormItemsValues, keyof FormItemsValues>
  ) => {
    // @ts-expect-error IDK what is the problem, but ts doesn't allow me to access the value property of e.target
    const { value } = e.target;

    if (!PRICE_REGEX.test(value)) {
      return;
    }

    if (!getValues("onlywhole")) {
      field.onChange(e);
      return;
    }

    if (!value.includes(",") && !value.includes(".")) {
      field.onChange(e);
    }
  };

  const handleChange = (
    e: FormEvent<HTMLInputElement>,
    field: ControllerRenderProps<FormItemsValues, keyof FormItemsValues>
  ) => {
    field.onChange(e);
  };

  return (
    <div>
      <VerticalSpace size={15} />
      <div className={styles.productPropertiesContainer}>
        {properties.map((item: [string, keyof FormItemsValues][], idx) => (
          <div key={idx}>
            {item.map(([label, name], index) => {
              if (label === "ID") {
                return (
                  <div key={index} className="mb-3">
                    <Controller
                      name="itemid"
                      control={control}
                      render={({ field }) => {
                        return (
                          <NamedInput
                            {...field}
                            label={label}
                            placeholder={label}
                            className={styles.propertyInputItem}
                            disabled
                          />
                        );
                      }}
                    />
                  </div>
                );
              }
              if (label === "В группе") {
                return (
                  <div key={index} className="mb-3">
                    <Controller
                      name="itemgroupid"
                      control={control}
                      render={({ field }) => (
                        <AppSelect
                          {...field}
                          label={label + ":"}
                          options={groupOptions}
                          className={styles.select}
                          placeholder="Выберите группу"
                          onChange={(option) => field.onChange(option)}
                        />
                      )}
                    />
                  </div>
                );
              }
              if (label === "Ставка НДС") {
                return (
                  <div key={index} className="mb-3">
                    <Controller
                      name="ndsid"
                      control={control}
                      render={({ field }) => (
                        <AppSelect
                          {...field}
                          label={label + ":"}
                          options={ndsOptions}
                          className={styles.select}
                          placeholder="Выберите ставку НДС"
                          onChange={(option) => field.onChange(option)}
                        />
                      )}
                    />
                  </div>
                );
              }
              if (label === "Ед. изм.") {
                return (
                  <div key={index} className="mb-3">
                    <Controller
                      name="measureid"
                      control={control}
                      render={({ field }) => (
                        <AppSelect
                          {...field}
                          label={label + ":"}
                          options={measureOptions}
                          className={styles.select}
                          placeholder="Выберите ед. изм."
                          onChange={(option) => field.onChange(option)}
                        />
                      )}
                    />
                  </div>
                );
              }
              return (
                <div key={index} className="mb-3">
                  <Controller
                    name={name}
                    control={control}
                    rules={{ required: false }}
                    render={({ field }) => {
                      const inputValue =
                        typeof field.value === "string" ||
                        typeof field.value === "number"
                          ? field.value
                          : "";
                      return (
                        <NamedInput
                          {...field}
                          label={label}
                          placeholder={label}
                          type={
                            name === "name" || name === "price"
                              ? "text"
                              : "number"
                          }
                          isRequired={false}
                          onChange={(e) => {
                            handleChange(e, field);
                          }} /*
                          onChange={(e) => {
                            name === "price"
                              ? handlePriceChange(e, field)
                              : handleChange(e, field);
                          }}*/
                          value={inputValue}
                          className={styles.propertyInputItem}
                        />
                      );
                    }}
                  />
                </div>
              );
            })}
          </div>
        ))}
        <div className="pr-5 overflow-y-scroll">
          {productImages[0]?.link && (
            <img
              src={productImages[0].link}
              alt="image"
              className={styles.first_image}
            />
          )}
          <div className="flex justify-between flex-wrap max-w-40">
            {filteredImages.map((imageItem) => (
              <img
                className="w-12 border border-gray-300 rounded-lg mt-2 object-cover"
                src={imageItem.link}
                key={imageItem.link}
                alt="image"
              />
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-x-24 gap-y-4 my-6">
          <Controller
            name="available"
            control={control}
            render={({ field }) => (
              <NamedCheckbox
                name="Доступен в терминале"
                checked={!!field.value}
                onChange={(checked) => field.onChange(checked)}
              />
            )}
          />

          <Controller
            name="service"
            control={control}
            render={({ field }) => (
              <NamedCheckbox
                name="Услуга"
                checked={!!field.value}
                onChange={(checked) => field.onChange(checked)}
              />
            )}
          />

          <Controller
            name="nodiscount"
            control={control}
            render={({ field }) => (
              <NamedCheckbox
                name="Не участвует в скидках"
                checked={!!field.value}
                onChange={(checked) => field.onChange(checked)}
              />
            )}
          />

          <Controller
            name="onlywhole"
            control={control}
            render={({ field }) => (
              <NamedCheckbox
                name="Продавать только целое количество"
                checked={!!field.value}
                onChange={(checked) => field.onChange(checked)}
              />
            )}
          />

          <Controller
            name="upload"
            control={control}
            render={({ field }) => (
              <NamedCheckbox
                name="Выгружать на сайт"
                checked={!!field.value}
                onChange={(checked) => field.onChange(checked)}
              />
            )}
          />

          <Controller
            name="nobonus"
            control={control}
            render={({ field }) => (
              <NamedCheckbox
                name="Нельзя оплатить бонусами"
                checked={!!field.value}
                onChange={(checked) => field.onChange(checked)}
              />
            )}
          />
        </div>
      </div>
    </div>
  );
};
